import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function OrderList() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [statuses, setStatuses] = useState([]);
  const [clients, setClients] = useState([]);

  // Filters
  const [search, setSearch] = useState('');
  const [systemState, setSystemState] = useState('');
  const [statusId, setStatusId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [fulfillmentStatus, setFulfillmentStatus] = useState('');
  const [page, setPage] = useState(1);

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showConfirm, setShowConfirm] = useState(null);
  const [showCancel, setShowCancel] = useState(null);
  const [editOrder, setEditOrder] = useState(null);

  // Create form
  const [form, setForm] = useState(getEmptyForm());
  const [lines, setLines] = useState([getEmptyLine()]);
  const [installments, setInstallments] = useState([]);
  const [saving, setSaving] = useState(false);

  function getEmptyForm() {
    return { client_id: '', department_id: '', currency: 'USD', order_date: new Date().toISOString().split('T')[0],
      expected_delivery_date: '', delivery_address: '', status_id: '', discount_type: '', discount_value: 0,
      tax_name: '', tax_percentage: 0, po_number: '', po_date: '', po_amount: '', notes: '' };
  }
  function getEmptyLine() {
    return { line_type: 'product', description: '', product_code: '', unit: 'pcs', quantity: 1, selling_rate: 0, cost_per_unit: 0 };
  }

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/orders?page=${page}&per_page=15`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (systemState) url += `&system_state=${systemState}`;
      if (statusId) url += `&status_id=${statusId}`;
      if (paymentStatus) url += `&payment_status=${paymentStatus}`;
      if (fulfillmentStatus) url += `&fulfillment_status=${fulfillmentStatus}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      setOrders(Array.isArray(json.data) ? json.data : []);
      setPagination({ current_page: json.current_page, last_page: json.last_page });
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId, page, search, systemState, statusId, paymentStatus, fulfillmentStatus]);

  const fetchStatuses = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/statuses`, { headers: getAuthHeaders() });
      if (res.ok) { const j = await res.json(); setStatuses(Array.isArray(j) ? j : []); }
    } catch (e) {}
  }, [companyId]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/clients?per_page=100`, { headers: getAuthHeaders() });
      if (res.ok) { const j = await res.json(); setClients(Array.isArray(j.data) ? j.data : (Array.isArray(j) ? j : [])); }
    } catch (e) {}
  }, [companyId]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { fetchStatuses(); fetchClients(); }, [fetchStatuses, fetchClients]);

  const fmt = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  // ── Create / Edit Order ──
  const handleSave = async () => {
    if (!lines.some(l => l.description)) return alert('At least one line item with a description is required');
    setSaving(true);
    try {
      const payload = { ...form, lines, installments: installments.length > 0 ? installments : undefined };
      const isEdit = !!editOrder;
      const url = isEdit ? `${API}/companies/${companyId}/orders/${editOrder.id}` : `${API}/companies/${companyId}/orders`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const err = await res.json(); alert(err.message || 'Error'); setSaving(false); return; }
      setShowCreate(false); setEditOrder(null); setForm(getEmptyForm()); setLines([getEmptyLine()]); setInstallments([]);
      fetchOrders();
    } catch (e) { alert('Error saving order'); }
    setSaving(false);
  };

  // ── Confirm Order ──
  const handleConfirm = async (id) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/${id}/confirm`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (!res.ok) { const err = await res.json(); alert(err.message); return; }
      setShowConfirm(null); fetchOrders();
    } catch (e) { alert('Error'); }
  };

  // ── Cancel Order ──
  const [cancelReason, setCancelReason] = useState('');
  const handleCancel = async (id) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/${id}/cancel`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: cancelReason }),
      });
      if (!res.ok) { const err = await res.json(); alert(err.message); return; }
      setShowCancel(null); setCancelReason(''); fetchOrders();
    } catch (e) { alert('Error'); }
  };

  // ── Complete Order ──
  const handleComplete = async (id) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/${id}/complete`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (!res.ok) { const err = await res.json(); alert(err.message); return; }
      fetchOrders();
    } catch (e) { alert('Error'); }
  };

  // ── Delete Draft ──
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this draft order?')) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/${id}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (!res.ok) { const err = await res.json(); alert(err.message); return; }
      fetchOrders();
    } catch (e) { alert('Error'); }
  };

  // ── Open Detail ──
  const openDetail = async (id) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/${id}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      setShowDetail(json.order);
    } catch (e) { alert('Error loading order'); }
  };

  // ── Open Edit ──
  const openEdit = async (id) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/${id}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const ord = (await res.json()).order;
      setForm({
        client_id: ord.client_id || '', department_id: ord.department_id || '', currency: ord.currency || 'USD',
        order_date: ord.order_date?.split('T')[0] || '', expected_delivery_date: ord.expected_delivery_date?.split('T')[0] || '',
        delivery_address: ord.delivery_address || '', status_id: ord.status_id || '',
        discount_type: ord.discount_type || '', discount_value: ord.discount_value || 0,
        tax_name: ord.tax_name || '', tax_percentage: ord.tax_percentage || 0,
        po_number: ord.po_number || '', po_date: ord.po_date?.split('T')[0] || '', po_amount: ord.po_amount || '', notes: ord.notes || '',
      });
      setLines(Array.isArray(ord.lines) && ord.lines.length > 0 ? ord.lines.map(l => ({
        line_type: l.line_type, description: l.description, product_code: l.product_code || '',
        unit: l.unit || 'pcs', quantity: l.quantity, selling_rate: l.selling_rate, cost_per_unit: l.cost_per_unit,
        delivered_qty: l.delivered_qty || 0,
      })) : [getEmptyLine()]);
      setInstallments(Array.isArray(ord.settlement_schedule) ? ord.settlement_schedule.map(s => ({
        installment_number: s.installment_number, amount: s.amount, percentage: s.percentage,
        due_date: s.due_date?.split('T')[0] || '', notes: s.notes || '',
      })) : []);
      setEditOrder(ord);
      setShowCreate(true);
    } catch (e) { alert('Error loading order'); }
  };

  // ── Line helpers ──
  const updateLine = (idx, field, val) => {
    const n = [...lines]; n[idx] = { ...n[idx], [field]: val }; setLines(n);
  };
  const addLine = () => setLines([...lines, getEmptyLine()]);
  const removeLine = (idx) => { if (lines.length > 1) setLines(lines.filter((_, i) => i !== idx)); };

  // ── Installment helpers ──
  const addInstallment = () => setInstallments([...installments, { installment_number: installments.length + 1, amount: 0, percentage: 0, due_date: '', notes: '' }]);
  const updateInstallment = (idx, field, val) => {
    const n = [...installments]; n[idx] = { ...n[idx], [field]: val }; setInstallments(n);
  };
  const removeInstallment = (idx) => setInstallments(installments.filter((_, i) => i !== idx));

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>📦 Orders</h2>
        <button onClick={() => { setForm(getEmptyForm()); setLines([getEmptyLine()]); setInstallments([]); setEditOrder(null); setShowCreate(true); }}
          style={btnPrimary}>+ New Order</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input type="text" placeholder="Search..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={inputStyle} />
        <select value={systemState} onChange={e => { setSystemState(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All States</option>
          <option value="draft">Draft</option><option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option><option value="cancelled">Cancelled</option>
        </select>
        <select value={statusId} onChange={e => { setStatusId(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Statuses</option>
          {statuses.filter(s => s.is_active).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={paymentStatus} onChange={e => { setPaymentStatus(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Payments</option>
          <option value="unpaid">Unpaid</option><option value="partially_paid">Partially Paid</option><option value="paid">Paid</option>
        </select>
        <select value={fulfillmentStatus} onChange={e => { setFulfillmentStatus(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Fulfillment</option>
          <option value="not_fulfilled">Not Fulfilled</option><option value="partially_fulfilled">Partially</option><option value="fully_fulfilled">Fully</option>
        </select>
      </div>

      {/* Table */}
      {initialLoading ? <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading...</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>{['Order #','Date','Client','Total','Cost','Profit','State','Status','Payment','Fulfillment','Actions'].map(h =>
                <th key={h} style={thStyle}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} style={{ cursor: 'pointer' }} onDoubleClick={() => openDetail(o.id)}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: '#3b82f6' }}>{o.order_number}</td>
                  <td style={tdStyle}>{o.order_date?.split('T')[0]}</td>
                  <td style={tdStyle}>{o.client_name || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(o.order_total)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(o.total_cost)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: o.gross_profit >= 0 ? '#10b981' : '#ef4444' }}>{fmt(o.gross_profit)}</td>
                  <td style={tdStyle}><Badge value={o.system_state} /></td>
                  <td style={tdStyle}>{o.status_name || '—'}</td>
                  <td style={tdStyle}><Badge value={o.payment_status} /></td>
                  <td style={tdStyle}><Badge value={o.fulfillment_status} /></td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button onClick={() => openDetail(o.id)} style={btnSm}>View</button>
                      {(o.system_state === 'draft' || o.system_state === 'confirmed') &&
                        <button onClick={() => openEdit(o.id)} style={btnSm}>Edit</button>}
                      {o.system_state === 'draft' &&
                        <button onClick={() => setShowConfirm(o)} style={{ ...btnSm, background: '#3b82f6', color: '#fff' }}>Confirm</button>}
                      {o.system_state === 'confirmed' &&
                        <button onClick={() => handleComplete(o.id)} style={{ ...btnSm, background: '#10b981', color: '#fff' }}>Complete</button>}
                      {(o.system_state === 'draft' || o.system_state === 'confirmed') &&
                        <button onClick={() => setShowCancel(o)} style={{ ...btnSm, background: '#ef4444', color: '#fff' }}>Cancel</button>}
                      {o.system_state === 'draft' &&
                        <button onClick={() => handleDelete(o.id)} style={{ ...btnSm, color: '#ef4444' }}>Delete</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={11} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No orders found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={btnSm}>← Prev</button>
          <span style={{ padding: '6px 12px', fontSize: 13 }}>Page {pagination.current_page} of {pagination.last_page}</span>
          <button disabled={page >= pagination.last_page} onClick={() => setPage(page + 1)} style={btnSm}>Next →</button>
        </div>
      )}

      {/* ═══ Create / Edit Modal ═══ */}
      {showCreate && (
        <Modal title={editOrder ? `Edit Order ${editOrder.order_number}` : 'New Order'} onClose={() => { setShowCreate(false); setEditOrder(null); }} wide>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            <label style={labelStyle}>Client
              <select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })} style={inputStyle}>
                <option value="">— Select Client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name || c.company_name}</option>)}
              </select>
            </label>
            <label style={labelStyle}>Order Date *
              <input type="date" value={form.order_date} onChange={e => setForm({ ...form, order_date: e.target.value })} style={inputStyle} required />
            </label>
            <label style={labelStyle}>Currency
              <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} style={inputStyle}>
                {['USD','LBP','EUR','GBP','AED','SAR'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label style={labelStyle}>Expected Delivery Date
              <input type="date" value={form.expected_delivery_date} onChange={e => setForm({ ...form, expected_delivery_date: e.target.value })} style={inputStyle} />
            </label>
            <label style={labelStyle}>Custom Status
              <select value={form.status_id} onChange={e => setForm({ ...form, status_id: e.target.value })} style={inputStyle}>
                <option value="">— None —</option>
                {statuses.filter(s => s.is_active).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <label style={labelStyle}>Delivery Address
              <input value={form.delivery_address} onChange={e => setForm({ ...form, delivery_address: e.target.value })} style={inputStyle} />
            </label>
          </div>

          {/* PO Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            <label style={labelStyle}>PO Number <input value={form.po_number} onChange={e => setForm({ ...form, po_number: e.target.value })} style={inputStyle} /></label>
            <label style={labelStyle}>PO Date <input type="date" value={form.po_date} onChange={e => setForm({ ...form, po_date: e.target.value })} style={inputStyle} /></label>
            <label style={labelStyle}>PO Amount <input type="number" value={form.po_amount} onChange={e => setForm({ ...form, po_amount: e.target.value })} style={inputStyle} /></label>
            <label style={labelStyle}>Notes <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={inputStyle} /></label>
          </div>

          {/* Line Items */}
          <h4 style={{ fontSize: 14, fontWeight: 600, margin: '16px 0 8px', color: '#374151' }}>Line Items</h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>{['Type','Description *','Code','Unit','Qty','Rate','Cost','Revenue','Profit',''].map(h =>
                  <th key={h} style={{ ...thStyle, fontSize: 11, padding: '6px 8px' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const rev = (l.quantity || 0) * (l.selling_rate || 0);
                  const cost = (l.quantity || 0) * (l.cost_per_unit || 0);
                  return (
                    <tr key={i}>
                      <td style={tdSm}><select value={l.line_type} onChange={e => updateLine(i, 'line_type', e.target.value)} style={{ ...inputStyle, width: 90, fontSize: 11 }}>
                        <option value="product">Product</option><option value="service">Service</option><option value="free_description">Free</option>
                      </select></td>
                      <td style={tdSm}><input value={l.description} onChange={e => updateLine(i, 'description', e.target.value)} style={{ ...inputStyle, minWidth: 140, fontSize: 11 }} /></td>
                      <td style={tdSm}><input value={l.product_code} onChange={e => updateLine(i, 'product_code', e.target.value)} style={{ ...inputStyle, width: 70, fontSize: 11 }} /></td>
                      <td style={tdSm}><input value={l.unit} onChange={e => updateLine(i, 'unit', e.target.value)} style={{ ...inputStyle, width: 50, fontSize: 11 }} /></td>
                      <td style={tdSm}><input type="number" value={l.quantity} onChange={e => updateLine(i, 'quantity', Number(e.target.value))} style={{ ...inputStyle, width: 60, fontSize: 11 }} /></td>
                      <td style={tdSm}><input type="number" value={l.selling_rate} onChange={e => updateLine(i, 'selling_rate', Number(e.target.value))} style={{ ...inputStyle, width: 80, fontSize: 11 }} /></td>
                      <td style={tdSm}><input type="number" value={l.cost_per_unit} onChange={e => updateLine(i, 'cost_per_unit', Number(e.target.value))} style={{ ...inputStyle, width: 80, fontSize: 11 }} /></td>
                      <td style={{ ...tdSm, textAlign: 'right', fontWeight: 500 }}>{fmt(rev)}</td>
                      <td style={{ ...tdSm, textAlign: 'right', color: rev - cost >= 0 ? '#10b981' : '#ef4444' }}>{fmt(rev - cost)}</td>
                      <td style={tdSm}><button onClick={() => removeLine(i)} style={{ ...btnSm, color: '#ef4444', padding: '2px 6px' }}>✕</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button onClick={addLine} style={{ ...btnSm, marginTop: 8 }}>+ Add Line</button>

          {/* Discount & Tax */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginTop: 16 }}>
            <label style={labelStyle}>Discount Type
              <select value={form.discount_type} onChange={e => setForm({ ...form, discount_type: e.target.value })} style={inputStyle}>
                <option value="">None</option><option value="percentage">Percentage</option><option value="fixed">Fixed</option>
              </select>
            </label>
            <label style={labelStyle}>Discount Value
              <input type="number" value={form.discount_value} onChange={e => setForm({ ...form, discount_value: Number(e.target.value) })} style={inputStyle} />
            </label>
            <label style={labelStyle}>Tax Name
              <input value={form.tax_name} onChange={e => setForm({ ...form, tax_name: e.target.value })} style={inputStyle} placeholder="e.g. VAT" />
            </label>
            <label style={labelStyle}>Tax %
              <input type="number" step="0.01" value={form.tax_percentage} onChange={e => setForm({ ...form, tax_percentage: Number(e.target.value) })} style={inputStyle} />
            </label>
          </div>

          {/* Settlement Schedule */}
          <h4 style={{ fontSize: 14, fontWeight: 600, margin: '16px 0 8px', color: '#374151' }}>Settlement Schedule (Optional)</h4>
          {installments.map((inst, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 600, width: 20 }}>#{inst.installment_number}</span>
              <input type="number" placeholder="Amount" value={inst.amount} onChange={e => updateInstallment(i, 'amount', Number(e.target.value))} style={{ ...inputStyle, width: 100 }} />
              <input type="number" placeholder="%" value={inst.percentage} onChange={e => updateInstallment(i, 'percentage', Number(e.target.value))} style={{ ...inputStyle, width: 60 }} />
              <input type="date" value={inst.due_date} onChange={e => updateInstallment(i, 'due_date', e.target.value)} style={{ ...inputStyle, width: 130 }} />
              <input placeholder="Notes" value={inst.notes} onChange={e => updateInstallment(i, 'notes', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
              <button onClick={() => removeInstallment(i)} style={{ ...btnSm, color: '#ef4444' }}>✕</button>
            </div>
          ))}
          <button onClick={addInstallment} style={{ ...btnSm, marginBottom: 16 }}>+ Add Installment</button>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <button onClick={() => { setShowCreate(false); setEditOrder(null); }} style={btnSm}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : editOrder ? 'Save Changes' : 'Create Order'}</button>
          </div>
        </Modal>
      )}

      {/* ═══ Confirm Modal ═══ */}
      {showConfirm && (
        <Modal title="Confirm Order" onClose={() => setShowConfirm(null)}>
          <p style={{ marginBottom: 16 }}>Confirm order <strong>{showConfirm.order_number}</strong>?</p>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>This will snapshot costs and make the order official. Product orders will reserve inventory.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={() => setShowConfirm(null)} style={btnSm}>Cancel</button>
            <button onClick={() => handleConfirm(showConfirm.id)} style={btnPrimary}>Confirm Order</button>
          </div>
        </Modal>
      )}

      {/* ═══ Cancel Modal ═══ */}
      {showCancel && (
        <Modal title="Cancel Order" onClose={() => { setShowCancel(null); setCancelReason(''); }}>
          <p style={{ marginBottom: 12 }}>Cancel order <strong>{showCancel.order_number}</strong>?</p>
          <label style={labelStyle}>Reason
            <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} style={{ ...inputStyle, minHeight: 60 }} />
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button onClick={() => { setShowCancel(null); setCancelReason(''); }} style={btnSm}>Close</button>
            <button onClick={() => handleCancel(showCancel.id)} style={{ ...btnPrimary, background: '#ef4444' }}>Cancel Order</button>
          </div>
        </Modal>
      )}

      {/* ═══ Detail Modal ═══ */}
      {showDetail && (
        <Modal title={`Order ${showDetail.order_number}`} onClose={() => setShowDetail(null)} wide>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div><span style={detailLabel}>System State</span><Badge value={showDetail.system_state} /></div>
            <div><span style={detailLabel}>Custom Status</span><span style={detailVal}>{showDetail.status_name || '—'}</span></div>
            <div><span style={detailLabel}>Client</span><span style={detailVal}>{showDetail.client_name || '—'}</span></div>
            <div><span style={detailLabel}>Order Date</span><span style={detailVal}>{showDetail.order_date?.split('T')[0]}</span></div>
            <div><span style={detailLabel}>Currency</span><span style={detailVal}>{showDetail.currency}</span></div>
            <div><span style={detailLabel}>Expected Delivery</span><span style={detailVal}>{showDetail.expected_delivery_date?.split('T')[0] || '—'}</span></div>
            <div><span style={detailLabel}>Subtotal</span><span style={detailVal}>{fmt(showDetail.subtotal)}</span></div>
            <div><span style={detailLabel}>Discount</span><span style={detailVal}>{showDetail.discount_type ? `${showDetail.discount_type} ${showDetail.discount_value} (${fmt(showDetail.discount_amount)})` : '—'}</span></div>
            <div><span style={detailLabel}>Tax</span><span style={detailVal}>{showDetail.tax_name ? `${showDetail.tax_name} ${showDetail.tax_percentage}% (${fmt(showDetail.tax_amount)})` : '—'}</span></div>
            <div><span style={detailLabel}>Order Total</span><span style={{ ...detailVal, fontWeight: 700, fontSize: 16 }}>{fmt(showDetail.order_total)}</span></div>
            <div><span style={detailLabel}>Total Cost</span><span style={detailVal}>{fmt(showDetail.total_cost)}</span></div>
            <div><span style={detailLabel}>Gross Profit</span><span style={{ ...detailVal, color: showDetail.gross_profit >= 0 ? '#10b981' : '#ef4444' }}>{fmt(showDetail.gross_profit)} ({showDetail.margin_percentage}%)</span></div>
            <div><span style={detailLabel}>Payment</span><Badge value={showDetail.payment_status} /></div>
            <div><span style={detailLabel}>Paid</span><span style={detailVal}>{fmt(showDetail.paid_amount)}</span></div>
            <div><span style={detailLabel}>Remaining</span><span style={detailVal}>{fmt(showDetail.remaining_amount)}</span></div>
            <div><span style={detailLabel}>Fulfillment</span><Badge value={showDetail.fulfillment_status} /></div>
            <div><span style={detailLabel}>Delivery</span><Badge value={showDetail.delivery_status} /></div>
            <div><span style={detailLabel}>Invoice</span><Badge value={showDetail.invoice_status} /></div>
          </div>

          {showDetail.po_number && (
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, marginBottom: 16 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Client PO:</span> {showDetail.po_number}
              {showDetail.po_date && ` | Date: ${showDetail.po_date.split('T')[0]}`}
              {showDetail.po_amount > 0 && ` | Amount: ${fmt(showDetail.po_amount)}`}
            </div>
          )}

          {/* Lines */}
          <h4 style={{ fontSize: 14, fontWeight: 600, margin: '12px 0 8px' }}>Line Items</h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>{['Type','Description','Qty','Rate','Cost','Revenue','Profit','Margin','Delivered','Remaining'].map(h =>
                  <th key={h} style={{ ...thStyle, fontSize: 11, padding: '6px 8px' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {Array.isArray(showDetail.lines) && showDetail.lines.map(l => (
                  <tr key={l.id}>
                    <td style={tdSm}><Badge value={l.line_type} /></td>
                    <td style={tdSm}>{l.description}{l.cost_missing ? ' ⚠️' : ''}</td>
                    <td style={{ ...tdSm, textAlign: 'right' }}>{l.quantity} {l.unit}</td>
                    <td style={{ ...tdSm, textAlign: 'right' }}>{fmt(l.selling_rate)}</td>
                    <td style={{ ...tdSm, textAlign: 'right' }}>{fmt(l.cost_per_unit)}</td>
                    <td style={{ ...tdSm, textAlign: 'right' }}>{fmt(l.line_revenue)}</td>
                    <td style={{ ...tdSm, textAlign: 'right', color: l.line_gross_profit >= 0 ? '#10b981' : '#ef4444' }}>{fmt(l.line_gross_profit)}</td>
                    <td style={{ ...tdSm, textAlign: 'right' }}>{l.line_margin}%</td>
                    <td style={{ ...tdSm, textAlign: 'right' }}>{l.line_type === 'product' ? l.delivered_qty : '—'}</td>
                    <td style={{ ...tdSm, textAlign: 'right' }}>{l.line_type === 'product' ? l.remaining_qty : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Settlement Schedule */}
          {Array.isArray(showDetail.settlement_schedule) && showDetail.settlement_schedule.length > 0 && (
            <>
              <h4 style={{ fontSize: 14, fontWeight: 600, margin: '16px 0 8px' }}>Settlement Schedule</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>{['#','Amount','%','Due Date','Status','Notes'].map(h => <th key={h} style={{ ...thStyle, fontSize: 11, padding: '6px 8px' }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {showDetail.settlement_schedule.map(s => (
                    <tr key={s.id}>
                      <td style={tdSm}>{s.installment_number}</td>
                      <td style={tdSm}>{fmt(s.amount)}</td>
                      <td style={tdSm}>{s.percentage}%</td>
                      <td style={tdSm}>{s.due_date?.split('T')[0]}</td>
                      <td style={tdSm}><Badge value={s.status} /></td>
                      <td style={tdSm}>{s.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Deliveries */}
          {Array.isArray(showDetail.deliveries) && showDetail.deliveries.length > 0 && (
            <>
              <h4 style={{ fontSize: 14, fontWeight: 600, margin: '16px 0 8px' }}>Deliveries</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>{['Delivery #','Date','Status','Delivered By','Items'].map(h => <th key={h} style={{ ...thStyle, fontSize: 11, padding: '6px 8px' }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {showDetail.deliveries.map(d => (
                    <tr key={d.id}>
                      <td style={tdSm}>{d.delivery_number}</td>
                      <td style={tdSm}>{d.delivery_date?.split('T')[0]}</td>
                      <td style={tdSm}><Badge value={d.status} /></td>
                      <td style={tdSm}>{d.delivered_by_name || '—'}</td>
                      <td style={tdSm}>{d.lines?.map(l => `${l.product_name}: ${l.quantity_delivered}`).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Versions */}
          {Array.isArray(showDetail.versions) && showDetail.versions.length > 0 && (
            <>
              <h4 style={{ fontSize: 14, fontWeight: 600, margin: '16px 0 8px' }}>Version History</h4>
              {showDetail.versions.map(v => (
                <div key={v.id} style={{ background: '#f9fafb', borderRadius: 6, padding: 10, marginBottom: 6, fontSize: 12 }}>
                  <strong>v{v.version_number}</strong> by {v.user?.name || 'System'} on {v.created_at?.split('T')[0]}
                  {v.change_reason && <span style={{ color: '#6b7280' }}> — {v.change_reason}</span>}
                </div>
              ))}
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button onClick={() => setShowDetail(null)} style={btnSm}>Close</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Components ──
function Modal({ title, children, onClose, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: wide ? 900 : 500,
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Badge({ value }) {
  const colors = {
    draft: '#9ca3af', confirmed: '#3b82f6', completed: '#10b981', cancelled: '#ef4444',
    unpaid: '#ef4444', partially_paid: '#f59e0b', paid: '#10b981',
    not_fulfilled: '#9ca3af', partially_fulfilled: '#f59e0b', fully_fulfilled: '#10b981',
    not_invoiced: '#9ca3af', partially_invoiced: '#f59e0b', fully_invoiced: '#10b981',
    not_started: '#9ca3af', partially_delivered: '#f59e0b', delivered: '#10b981', overdue: '#ef4444',
    reversed: '#ef4444', pending: '#f59e0b', invoiced: '#3b82f6',
    product: '#3b82f6', service: '#8b5cf6', free_description: '#6b7280',
  };
  const c = colors[value] || '#6b7280';
  return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: c + '18', color: c }}>{(value || '').replace(/_/g, ' ')}</span>;
}

// ── Styles ──
const btnPrimary = { padding: '8px 20px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSm = { padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', fontSize: 12, cursor: 'pointer' };
const inputStyle = { padding: '7px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, width: '100%', boxSizing: 'border-box' };
const selectStyle = { padding: '7px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, background: '#fff' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 500, color: '#374151' };
const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', fontSize: 12, fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151', whiteSpace: 'nowrap' };
const tdSm = { padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: '#374151' };
const detailLabel = { display: 'block', fontSize: 11, fontWeight: 600, color: '#9ca3af', marginBottom: 2 };
const detailVal = { fontSize: 14, color: '#374151' };
